import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
    IconButton,
} from "@mui/material";
import { Close, Person, CheckCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { transportOrderStateNames } from "../../../models/transport/transportOrder";
import { useAppSelector } from "../../../framework/customStore";
import { Base } from "../../../framework/base";
import { TransportOrderNotificationListItemDto } from "../../../models/transport/transportOrderNotification";
import { NotificationSource } from "../../../models/notification/notificationSource";

interface NotificationsDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirmAll: () => void;
    notificationContext: "toDriver" | "fromDriver";
    notifications: TransportOrderNotificationListItemDto[];
    orderNumber: number;
    vehicleName: string;
}

export const NotificationsDialog: React.FC<NotificationsDialogProps> = ({
    open,
    onClose,
    onConfirmAll,
    notificationContext,
    notifications = [],
    orderNumber,
    vehicleName
}) => {
    const { t } = useTranslation();
    const employees = useAppSelector((state) => state.employees.employees);
    const unconfirmedNotifications = notifications.filter((c) => c.state < 2);
    const confirmedNotifications = notifications.filter((c) => c.state >= 2);

    const getUsername = (notification: any) => {
        const { createdEmployee: createdEmployeeId } = notification;
        if (notification.source === NotificationSource.MobileToDesktop) {
            return employees?.find((e) => e.id === createdEmployeeId)?.fullName || t("notifications.unknownUser");
        }
        return employees?.find((e) => e.id === notification.employeeId)?.fullName || t("notifications.unknownUser");
    };

    const renderNotificationDetails = (notification: any) => {
        const { createdDateTime, createdEmployee: createdEmployeeId, modifiedDateTime, modifiedEmployeeId } = notification;
        const createdEmployeeName = employees?.find((e) => e.id === createdEmployeeId)?.fullName || t("notifications.unknownUser");
        const modifiedEmployeeName = employees?.find((e) => e.id === modifiedEmployeeId)?.fullName || t("notifications.unknownUser");
        return (
            <>
                <Typography variant="body2" color="textSecondary" sx={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
                    <Person fontSize="small" sx={{ marginRight: "4px" }} />
                    {createdEmployeeName} - {Base.dayjsDateToDateTimeStr(createdDateTime)}
                </Typography>
                {modifiedDateTime && modifiedEmployeeId && (
                    <Typography variant="body2" color="textSecondary" sx={{ display: "flex", alignItems: "center", marginTop: "4px" }}>
                        <CheckCircle fontSize="small" sx={{ marginRight: "4px", color: "green" }} />
                        {modifiedEmployeeName} - {Base.dayjsDateToDateTimeStr(modifiedDateTime)}
                    </Typography>
                )}
            </>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {notificationContext === "toDriver"
                    ? t("notifications.dialogTitle.toDriver")
                    : t("notifications.dialogTitle.fromDriver")}
                <IconButton onClick={onClose} sx={{ marginRight: "-12px" }}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <List>
                    {unconfirmedNotifications.length > 0 && (
                        <>
                            <Typography variant="h6" sx={{ marginTop: "16px", marginBottom: "8px" }}>
                                {t("notifications.unconfirmed")}
                            </Typography>
                            {unconfirmedNotifications.map((notification, index) => (
                                <ListItem
                                    key={`unconfirmed-${index}`}
                                    sx={{
                                        backgroundColor: "#ffe6e6",
                                        borderRadius: "4px",
                                        marginBottom: "8px",
                                    }}
                                >
                                    <ListItemText
                                        primary={t(notification.message, {
                                            orderNro: orderNumber,
                                            vehicleName: vehicleName,
                                            state: t(transportOrderStateNames[notification?.orderState]),
                                            employeeName: getUsername(notification),
                                        })}
                                        secondary={renderNotificationDetails(notification)}
                                        primaryTypographyProps={{ fontWeight: "bold" }}
                                    />
                                </ListItem>
                            ))}
                        </>
                    )}
                    {confirmedNotifications.length > 0 && (
                        <>
                            <Typography variant="h6" sx={{ marginTop: "16px", marginBottom: "8px" }}>
                                {t("notifications.confirmed")}
                            </Typography>
                            {confirmedNotifications.map((notification, index) => (
                                <ListItem
                                    key={`confirmed-${index}`}
                                    sx={{
                                        backgroundColor: "#f5f5f5",
                                        borderRadius: "4px",
                                        marginBottom: "8px",
                                    }}
                                >
                                    <ListItemText
                                        primary={t(notification.message, {
                                            orderNro: orderNumber,
                                            vehicleName: vehicleName,
                                            state: t(transportOrderStateNames[notification?.orderState]),
                                            employeeName: getUsername(notification),
                                        })}
                                        secondary={renderNotificationDetails(notification)}
                                        primaryTypographyProps={{ fontWeight: "normal" }}
                                    />
                                </ListItem>
                            ))}
                        </>
                    )}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("common.close")}</Button>
                <Button
                    onClick={onConfirmAll}
                    color="primary"
                    variant="contained"
                    disabled={unconfirmedNotifications?.length === 0}
                >
                    {t("notifications.confirmAll")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
